import { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { useAuthState } from '../hooks/useAuthState'
import { usePendingKnowledgeCount } from '../hooks/usePendingKnowledgeCount'
import { useCompany } from '../contexts/CompanyContext'
import SchoolIcon from '@mui/icons-material/School'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import PersonIcon from '@mui/icons-material/Person'
import LogoutIcon from '@mui/icons-material/Logout'
import './Navigation.css'

function Navigation () {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1400)
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false)
  const [superAdminDropdownOpen, setSuperAdminDropdownOpen] = useState(false)
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)

  const adminDropdownRef = useRef(null)
  const superAdminDropdownRef = useRef(null)
  const profileDropdownRef = useRef(null)

  const { user, logout } = useAuth()
  const {
    loading: authLoading,
    isModerator,
    isSuperAdmin,
    hasAdminAccess
  } = useAuthState()

  // Get pending knowledge count for notification badge
  const pendingCount = usePendingKnowledgeCount(user, user?.userData?.role)

  // Get company logo if available
  let logoUrl = null
  let features = {}
  try {
    const companyData = useCompany()
    logoUrl = companyData?.logoUrl || null
    features = companyData?.features || {}
  } catch (error) {
    // CompanyContext not available - that's fine, use fallback
  }

  const hasLogo = features?.logo && logoUrl

  const navigate = useNavigate()

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1400
      setIsMobile(mobile)
      if (!mobile) setIsMobileMenuOpen(false)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target)
      ) {
        setAdminDropdownOpen(false)
      }
      if (
        superAdminDropdownRef.current &&
        !superAdminDropdownRef.current.contains(event.target)
      ) {
        setSuperAdminDropdownOpen(false)
      }
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setProfileDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNavClick = () => {
    if (isMobile) setIsMobileMenuOpen(false)
    setAdminDropdownOpen(false)
    setSuperAdminDropdownOpen(false)
    setProfileDropdownOpen(false)
  }

  const handleLogout = async () => {
    try {
      console.log('🔐 Logging out...')
      await logout()
      console.log('✅ Successfully logged out')
      navigate('/login')
    } catch (error) {
      console.error('❌ Logout failed:', error)
      toast.error('Fehler beim Abmelden: ' + error.message)
    }
  }

  return (
    <>
      <nav className='navigation'>
        <div className='nav-content'>
          <Link to='/' className='nav-brand' onClick={handleNavClick}>
            {hasLogo ? (
              <img
                src={logoUrl}
                alt='Company Logo'
                className='nav-brand-logo'
                onError={(e) => {
                  // Fallback to text if logo fails to load
                  e.target.style.display = 'none'
                  const fallback = e.target.nextElementSibling
                  if (fallback) {
                    fallback.style.display = 'block'
                  }
                }}
              />
            ) : null}
            <h2 
              className='brand-title' 
              style={{ display: hasLogo ? 'none' : 'block' }}
            >
              AsclepionWiki
            </h2>
          </Link>
          {!isMobile && (
            <>
              <div className='nav-links'>
                {/* Extra Link für home (/) */}
                <NavLink
                  to='/search'
                  className={({ isActive }) =>
                    `nav-link-button ${isActive ? 'active' : ''}`
                  }
                >
                  Chat
                </NavLink>
                <NavLink
                  to='/contribute'
                  className={({ isActive }) =>
                    `nav-link-button ${isActive ? 'active' : ''}`
                  }
                >
                  Wissen hinzufügen
                </NavLink>

                <NavLink
                  to='/browse'
                  className={({ isActive }) =>
                    `nav-link-button ${isActive ? 'active' : ''}`
                  }
                >
                  Wissen durchsuchen
                </NavLink>

                {/* Moderatoren: Direkter Link zu Moderation */}
                {isModerator && !hasAdminAccess && (
                  <NavLink
                    to='/admin/moderation'
                    className={({ isActive }) =>
                      `nav-link-button ${isActive ? 'active' : ''}`
                    }
                  >
                    Moderieren
                    {pendingCount > 0 && (
                      <span className='notification-badge'>{pendingCount}</span>
                    )}
                  </NavLink>
                )}

                {/* Admin/SuperAdmin: Vollständiges Admin-Dropdown */}
                {hasAdminAccess && (
                  <div className='nav-dropdown' ref={adminDropdownRef}>
                    <button
                      className={`nav-dropdown-trigger ${
                        adminDropdownOpen ? 'open' : ''
                      }`}
                      onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                    >
                      Admin
                      {pendingCount > 0 && (
                        <span className='notification-badge'>
                          {pendingCount}
                        </span>
                      )}
                      <svg viewBox='0 0 24 24' className='dropdown-icon'>
                        <path d='M6 9l6 6 6-6' />
                      </svg>
                    </button>
                    {adminDropdownOpen && (
                      <div className='nav-dropdown-menu'>
                        <NavLink
                          to='/admin/moderation'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Moderieren{' '}
                          {pendingCount > 0 && (
                            <span className='notification-badge-small'>
                              {pendingCount}
                            </span>
                          )}
                        </NavLink>

                        <NavLink
                          to='/admin/devices'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Geräteverwaltung
                        </NavLink>
                        <NavLink
                          to='/admin/invite-codes'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Einladungscodes
                        </NavLink>
                        <NavLink
                          to='/admin/users'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Benutzerverwaltung
                        </NavLink>
                        <NavLink
                          to='/upload'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Upload
                        </NavLink>
                        <NavLink
                          to='/admin/personalization'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Personalisierung
                        </NavLink>
                      </div>
                    )}
                  </div>
                )}

                {isSuperAdmin && (
                  <div className='nav-dropdown' ref={superAdminDropdownRef}>
                    <button
                      className={`nav-dropdown-trigger ${
                        superAdminDropdownOpen ? 'open' : ''
                      }`}
                      onClick={() =>
                        setSuperAdminDropdownOpen(!superAdminDropdownOpen)
                      }
                    >
                      Super Admin
                      <svg viewBox='0 0 24 24' className='dropdown-icon'>
                        <path d='M6 9l6 6 6-6' />
                      </svg>
                    </button>
                    {superAdminDropdownOpen && (
                      <div className='nav-dropdown-menu'>
                        <NavLink
                          to='/superadmin/companies'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Companies
                        </NavLink>
                        <NavLink
                          to='/superadmin/contact-requests'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Kontaktanfragen
                        </NavLink>
                        <NavLink
                          to='/admin/analytics'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Analytics
                        </NavLink>
                        <NavLink
                          to='/superadmin/conversations'
                          className={({ isActive }) =>
                            `nav-dropdown-link ${isActive ? 'active' : ''}`
                          }
                          onClick={handleNavClick}
                        >
                          Konversationen
                        </NavLink>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className='nav-user-actions'>
                {/* Tutorials Icon Button */}
                <NavLink
                  to='/tutorials'
                  className={({ isActive }) =>
                    `nav-icon-button ${isActive ? 'active' : ''}`
                  }
                  title='Tutorials'
                >
                  <SchoolIcon />
                </NavLink>

                {/* Profile Dropdown */}
                <div className='nav-dropdown' ref={profileDropdownRef}>
                  <button
                    className={`nav-icon-button profile-trigger ${
                      profileDropdownOpen ? 'open' : ''
                    }`}
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    title='Profil'
                  >
                    <AccountCircleIcon />
                  </button>
                  {profileDropdownOpen && (
                    <div className='nav-dropdown-menu profile-menu'>
                      <div className='profile-menu-header'>
                        <div className='profile-menu-name'>
                          {user?.displayName || user?.email}
                        </div>
                        {user?.userData?.company && (
                          <div className='profile-menu-company'>
                            {user.userData.company}
                          </div>
                        )}
                      </div>
                      <div className='profile-menu-divider'></div>
                      <NavLink
                        to='/profile'
                        className={({ isActive }) =>
                          `nav-dropdown-link ${isActive ? 'active' : ''}`
                        }
                        onClick={handleNavClick}
                      >
                        <PersonIcon />
                        Mein Profil
                      </NavLink>
                      <button
                        className='nav-dropdown-link logout-link'
                        onClick={handleLogout}
                      >
                        <LogoutIcon />
                        Abmelden
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {isMobile && (
            <>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className='burger-button'
                aria-label='Menü öffnen'
              >
                {isMobileMenuOpen ? (
                  <svg viewBox='0 0 24 24' className='icon'>
                    <line x1='18' y1='6' x2='6' y2='18' />
                    <line x1='6' y1='6' x2='18' y2='18' />
                  </svg>
                ) : (
                  <svg viewBox='0 0 24 24' className='icon'>
                    <line x1='3' y1='6' x2='21' y2='6' />
                    <line x1='3' y1='12' x2='21' y2='12' />
                    <line x1='3' y1='18' x2='21' y2='18' />
                  </svg>
                )}
              </button>
            </>
          )}
        </div>
      </nav>
      {isMobile && isMobileMenuOpen && (
        <>
          <div className='overlay' onClick={() => setIsMobileMenuOpen(false)} />

          <div className='mobile-menu'>
            <div className='mobile-user-section'>
              <div className='mobile-user-avatar'>
                {(
                  user?.displayName?.[0] ||
                  user?.email?.[0] ||
                  'U'
                ).toUpperCase()}
              </div>
              <div>
                <div className='mobile-user-fullname'>
                  {user?.displayName || user?.email}
                </div>
                {user?.userData?.company && (
                  <div className='mobile-user-company'>
                    {user.userData.company}
                  </div>
                )}
              </div>
            </div>

            <div className='mobile-nav-links'>
              <NavLink
                to='/search'
                className={({ isActive }) =>
                  `mobile-nav-link ${isActive ? 'active' : ''}`
                }
                onClick={handleNavClick}
              >
                Chat
              </NavLink>

              <NavLink
                to='/contribute'
                className={({ isActive }) =>
                  `mobile-nav-link ${isActive ? 'active' : ''}`
                }
                onClick={handleNavClick}
              >
                Wissen hinzufügen
              </NavLink>

              <NavLink
                to='/browse'
                className={({ isActive }) =>
                  `mobile-nav-link ${isActive ? 'active' : ''}`
                }
                onClick={handleNavClick}
              >
                Wissen durchsuchen
              </NavLink>

              <NavLink
                to='/profile'
                className={({ isActive }) =>
                  `mobile-nav-link ${isActive ? 'active' : ''}`
                }
                onClick={handleNavClick}
              >
                Profil
              </NavLink>

              <NavLink
                to='/tutorials'
                className={({ isActive }) =>
                  `mobile-nav-link ${isActive ? 'active' : ''}`
                }
                onClick={handleNavClick}
              >
                Tutorials
              </NavLink>

              {/* Moderatoren: Nur Moderation */}
              {isModerator && !hasAdminAccess && (
                <>
                  <div className='mobile-divider' />
                  <div className='mobile-nav-section'>Moderation</div>

                  <NavLink
                    to='/admin/moderation'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Moderieren
                    {pendingCount > 0 && (
                      <span className='notification-badge-small'>
                        {pendingCount}
                      </span>
                    )}
                  </NavLink>
                </>
              )}

              {/* Admin/SuperAdmin: Vollständiger Admin-Bereich */}
              {hasAdminAccess && (
                <>
                  <div className='mobile-divider' />
                  <div className='mobile-nav-section'>Admin</div>

                  <NavLink
                    to='/upload'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Upload
                  </NavLink>

                  <NavLink
                    to='/admin/moderation'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Moderieren
                    {pendingCount > 0 && (
                      <span className='notification-badge-small'>
                        {pendingCount}
                      </span>
                    )}
                  </NavLink>

                  <NavLink
                    to='/admin/devices'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Geräte verwalten
                  </NavLink>
                  <NavLink
                    to='/admin/invite-codes'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Einladungscodes
                  </NavLink>
                  <NavLink
                    to='/admin/users'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Benutzerverwaltung
                  </NavLink>
                  <NavLink
                    to='/admin/personalization'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Personalisierung
                  </NavLink>
                </>
              )}

              {isSuperAdmin && (
                <>
                  <div className='mobile-divider' />
                  <div className='mobile-nav-section'>Super Admin</div>

                  <NavLink
                    to='/superadmin/companies'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Companies
                  </NavLink>

                  <NavLink
                    to='/superadmin/contact-requests'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Kontaktanfragen
                  </NavLink>

                  <NavLink
                    to='/admin/analytics'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Analytics
                  </NavLink>

                  <NavLink
                    to='/superadmin/conversations'
                    className={({ isActive }) =>
                      `mobile-nav-link ${isActive ? 'active' : ''}`
                    }
                    onClick={handleNavClick}
                  >
                    Konversationen
                  </NavLink>
                </>
              )}
            </div>

            <button onClick={handleLogout} className='mobile-logout-btn'>
              Abmelden
            </button>
          </div>
        </>
      )}

    </>
  )
}

export default Navigation
